import React, { useState, useRef, useEffect } from 'react';
import './CropModal.css';

interface CropModalProps {
  imageUrl: string;
  onCrop: (croppedDataUrl: string, metadata: { name: string; shape: string; feather: number }) => void;
  onClose: () => void;
  imageType?: string;
  originalName?: string;
}

const CropModal: React.FC<CropModalProps> = ({ imageUrl, onCrop, onClose, imageType = 'asset', originalName = 'image' }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const imageRef = useRef<HTMLImageElement | null>(null);
  const [crop, setCrop] = useState({ x: 50, y: 50, width: 200, height: 200 });
  const [dragging, setDragging] = useState(false);
  const [resizing, setResizing] = useState<'se' | null>(null);
  const dragStartRef = useRef({ x: 0, y: 0, cropX: 0, cropY: 0, cropWidth: 0, cropHeight: 0 });
  const [imageLoaded, setImageLoaded] = useState(false);
  const [cropShape, setCropShape] = useState<'rect' | 'ellipse'>('rect');
  const [feather, setFeather] = useState(0);
  const [borderWidth, setBorderWidth] = useState(0);
  const [borderStyle, setBorderStyle] = useState('solid');
  const [borderColor, setBorderColor] = useState('#000000');
  
  // Generate automatic name based on crop settings
  const generateCroppedName = () => {
    const shapeName = cropShape === 'rect' ? 'rect' : 'ellipse';
    const featherSuffix = feather > 0 ? `-feather${feather}` : '';
    const borderSuffix = borderWidth > 0 ? `-border${borderWidth}` : '';
    return `${originalName}-cropped-${shapeName}${featherSuffix}${borderSuffix}`;
  };
  
  const [croppedName, setCroppedName] = useState(generateCroppedName());

  useEffect(() => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      imageRef.current = img;
      setImageLoaded(true);
      drawCanvas();
    };
    img.src = imageUrl;
  }, [imageUrl]);

  useEffect(() => {
    if (imageLoaded) {
      drawCanvas();
    }
  }, [crop, imageLoaded, cropShape, feather]);
  
  // Update cropped name when shape, feather, or border changes
  useEffect(() => {
    setCroppedName(generateCroppedName());
  }, [cropShape, feather, borderWidth, originalName]);

  const drawCanvas = () => {
    const canvas = canvasRef.current;
    const img = imageRef.current;
    if (!canvas || !img) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Use natural dimensions to ensure correct aspect ratio
    const maxWidth = 700;
    const maxHeight = 600;
    const imgWidth = img.naturalWidth || img.width;
    const imgHeight = img.naturalHeight || img.height;
    const aspectRatio = imgWidth / imgHeight;
    
    let width = imgWidth;
    let height = imgHeight;

    // Scale down if too large while maintaining aspect ratio
    if (width > maxWidth || height > maxHeight) {
      if (aspectRatio > 1) {
        // Landscape: width is limiting factor
        width = Math.min(maxWidth, imgWidth);
        height = width / aspectRatio;
      } else {
        // Portrait: height is limiting factor
        height = Math.min(maxHeight, imgHeight);
        width = height * aspectRatio;
      }
    }

    // High-DPI support: scale canvas to device pixel ratio
    const dpr = (window.devicePixelRatio || 1);
    canvas.width = Math.floor(width * dpr);
    canvas.height = Math.floor(height * dpr);
    canvas.style.width = `${width}px`;
    canvas.style.height = `${height}px`;
    // Render commands use CSS pixel coordinates
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

    // Draw the image
    ctx.drawImage(img, 0, 0, width, height);

    // Draw semi-transparent white overlay
    ctx.fillStyle = 'rgba(255, 255, 255, 0.6)';
    ctx.fillRect(0, 0, width, height);

    // Clear crop area and draw with feather preview
    ctx.save();
    
    // Clear the overlay in crop area and draw the actual image
    ctx.clearRect(crop.x, crop.y, crop.width, crop.height);
    
    // Draw the cropped portion of the image
    ctx.drawImage(
      img,
      (crop.x / width) * img.width,
      (crop.y / height) * img.height,
      (crop.width / width) * img.width,
      (crop.height / height) * img.height,
      crop.x,
      crop.y,
      crop.width,
      crop.height
    );
    
    // For ellipse shape, reapply white overlay outside the ellipse
    if (cropShape === 'ellipse') {
      ctx.save();
      ctx.globalCompositeOperation = 'source-over';
      
      // Clip to everything EXCEPT the ellipse
      ctx.beginPath();
      ctx.rect(crop.x, crop.y, crop.width, crop.height);
      
      const centerX = crop.x + crop.width / 2;
      const centerY = crop.y + crop.height / 2;
      const radiusX = crop.width / 2;
      const radiusY = crop.height / 2;
      
      ctx.ellipse(centerX, centerY, radiusX, radiusY, 0, 0, 2 * Math.PI, true); // counterclockwise for clip
      ctx.clip('evenodd');
      
      // Draw white overlay only in the clipped area (outside ellipse)
      ctx.fillStyle = 'rgba(255, 255, 255, 0.6)';
      ctx.fillRect(crop.x, crop.y, crop.width, crop.height);
      
      ctx.restore();
    }
    
    // Apply feather preview if enabled
    if (feather > 0) {
      ctx.save();
      ctx.globalCompositeOperation = 'destination-out';
      
      if (cropShape === 'ellipse') {
        const centerX = crop.x + crop.width / 2;
        const centerY = crop.y + crop.height / 2;
        const radiusX = crop.width / 2;
        const radiusY = crop.height / 2;
        
        // Clip to only affect the crop area
        ctx.beginPath();
        ctx.rect(crop.x, crop.y, crop.width, crop.height);
        ctx.clip();
        
        // Create gradient for feather effect
        ctx.translate(centerX, centerY);
        if (radiusX > 0 && radiusY > 0) {
          ctx.scale(1, radiusY / radiusX);
        }
        
        const featherAmount = (feather / 100) * radiusX;
        const innerRadius = Math.max(0, radiusX - featherAmount);
        const gradient = ctx.createRadialGradient(0, 0, innerRadius, 0, 0, radiusX);
        gradient.addColorStop(0, 'rgba(0, 0, 0, 0)');
        gradient.addColorStop(1, 'rgba(0, 0, 0, 1)');
        
        ctx.fillStyle = gradient;
        ctx.beginPath();
        ctx.arc(0, 0, radiusX, 0, 2 * Math.PI);
        ctx.fill();
      } else {
        // Rectangle feather effect - fade out the edges
        const featherPx = (feather / 100) * Math.min(crop.width, crop.height) / 2;
        
        // Top edge
        const topGradient = ctx.createLinearGradient(crop.x, crop.y, crop.x, crop.y + featherPx);
        topGradient.addColorStop(0, 'rgba(0, 0, 0, 1)');
        topGradient.addColorStop(1, 'rgba(0, 0, 0, 0)');
        ctx.fillStyle = topGradient;
        ctx.fillRect(crop.x, crop.y, crop.width, featherPx);
        
        // Bottom edge
        const bottomGradient = ctx.createLinearGradient(crop.x, crop.y + crop.height - featherPx, crop.x, crop.y + crop.height);
        bottomGradient.addColorStop(0, 'rgba(0, 0, 0, 0)');
        bottomGradient.addColorStop(1, 'rgba(0, 0, 0, 1)');
        ctx.fillStyle = bottomGradient;
        ctx.fillRect(crop.x, crop.y + crop.height - featherPx, crop.width, featherPx);
        
        // Left edge
        const leftGradient = ctx.createLinearGradient(crop.x, crop.y, crop.x + featherPx, crop.y);
        leftGradient.addColorStop(0, 'rgba(0, 0, 0, 1)');
        leftGradient.addColorStop(1, 'rgba(0, 0, 0, 0)');
        ctx.fillStyle = leftGradient;
        ctx.fillRect(crop.x, crop.y, featherPx, crop.height);
        
        // Right edge
        const rightGradient = ctx.createLinearGradient(crop.x + crop.width - featherPx, crop.y, crop.x + crop.width, crop.y);
        rightGradient.addColorStop(0, 'rgba(0, 0, 0, 0)');
        rightGradient.addColorStop(1, 'rgba(0, 0, 0, 1)');
        ctx.fillStyle = rightGradient;
        ctx.fillRect(crop.x + crop.width - featherPx, crop.y, featherPx, crop.height);
      }
      
      ctx.restore();
    }

    // Draw crop border
    ctx.strokeStyle = '#007bff';
    ctx.lineWidth = 2;
    if (cropShape === 'ellipse') {
      ctx.beginPath();
      ctx.ellipse(
        crop.x + crop.width / 2,
        crop.y + crop.height / 2,
        crop.width / 2,
        crop.height / 2,
        0,
        0,
        2 * Math.PI
      );
      ctx.stroke();
    } else {
      ctx.strokeRect(crop.x, crop.y, crop.width, crop.height);
    }

    // Draw resize handle: circle centered on bottom-right corner
    const cornerX = crop.x + crop.width;
    const cornerY = crop.y + crop.height;
    // Hover/tolerance ring (visual aid)
    ctx.strokeStyle = 'rgba(0, 123, 255, 0.25)';
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.arc(cornerX, cornerY, 22, 0, Math.PI * 2); // visualize ~10-12px tolerance
    ctx.stroke();

    // Actual handle circle
    ctx.beginPath();
    ctx.arc(cornerX, cornerY, 10, 0, Math.PI * 2);
    ctx.fillStyle = '#007bff';
    ctx.shadowColor = 'rgba(0, 0, 0, 0.5)';
    ctx.shadowBlur = 4;
    ctx.fill();
    ctx.shadowBlur = 0;
  };

  const handleMouseDown = (e: React.MouseEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const rect = canvas.getBoundingClientRect();
    // Use CSS pixels to match drawing coordinates (we scale the context for DPR)
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    // Check if clicking resize handle (bottom-right corner) using circular hit area
    const cornerX = crop.x + crop.width;
    const cornerY = crop.y + crop.height;
    const handleRadius = 10; // visual radius
    const handleTolerance = 12; // extra clickable tolerance
    const dist = Math.hypot(x - cornerX, y - cornerY);
    const clickOnHandle = dist <= (handleRadius + handleTolerance);
    
    if (clickOnHandle) {
      setResizing('se');
      dragStartRef.current = { x, y, cropX: crop.x, cropY: crop.y, cropWidth: crop.width, cropHeight: crop.height };
      return;
    }

    // Check if clicking inside crop area (but not on resize handle)
    const isInCropArea = x >= crop.x && x <= crop.x + crop.width && y >= crop.y && y <= crop.y + crop.height;
    const isOnHandle = clickOnHandle;
    
    if (isInCropArea && !isOnHandle) {
      setDragging(true);
      dragStartRef.current = { x, y, cropX: crop.x, cropY: crop.y, cropWidth: crop.width, cropHeight: crop.height };
    }
  };

  const handleMouseMove = (e: React.MouseEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const rect = canvas.getBoundingClientRect();
    // Use CSS pixels to match drawing coordinates (we scale the context for DPR)
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    // Update cursor based on circular hit area around corner
    const cornerX = crop.x + crop.width;
    const cornerY = crop.y + crop.height;
    const handleRadius = 10;
    const handleTolerance = 12;
    const onHandle = Math.hypot(x - cornerX, y - cornerY) <= (handleRadius + handleTolerance);
    
    canvas.style.cursor = onHandle ? 'nwse-resize' : (dragging ? 'grabbing' : resizing ? 'nwse-resize' : 'grab');

    if (dragging) {
      const deltaX = x - dragStartRef.current.x;
      const deltaY = y - dragStartRef.current.y;
      const canvasW = rect.width;
      const canvasH = rect.height;
      const newX = Math.max(0, Math.min(canvasW - crop.width, dragStartRef.current.cropX + deltaX));
      const newY = Math.max(0, Math.min(canvasH - crop.height, dragStartRef.current.cropY + deltaY));
      setCrop({ ...crop, x: newX, y: newY });
    } else if (resizing) {
      const deltaX = x - dragStartRef.current.x;
      const deltaY = y - dragStartRef.current.y;
      const canvasW = rect.width;
      const canvasH = rect.height;
      const newWidth = Math.max(50, Math.min(canvasW - dragStartRef.current.cropX, dragStartRef.current.cropWidth + deltaX));
      const newHeight = Math.max(50, Math.min(canvasH - dragStartRef.current.cropY, dragStartRef.current.cropHeight + deltaY));
      setCrop({ x: dragStartRef.current.cropX, y: dragStartRef.current.cropY, width: newWidth, height: newHeight });
    }
  };

  const handleMouseUp = () => {
    setDragging(false);
    setResizing(null);
  };

  const handleApplyCrop = () => {
    const canvas = canvasRef.current;
    const img = imageRef.current;
    if (!canvas || !img) return;

    // Create a new canvas for the cropped image
    const croppedCanvas = document.createElement('canvas');
    const ctx = croppedCanvas.getContext('2d');
    if (!ctx) return;

    // Calculate crop in original image coordinates
    const scaleX = img.width / canvas.width;
    const scaleY = img.height / canvas.height;
    const cropX = crop.x * scaleX;
    const cropY = crop.y * scaleY;
    const cropWidth = crop.width * scaleX;
    const cropHeight = crop.height * scaleY;

    // Set canvas size exactly to the crop dimensions
    croppedCanvas.width = Math.round(cropWidth);
    croppedCanvas.height = Math.round(cropHeight);

    // Draw the cropped portion
    ctx.drawImage(
      img,
      cropX,
      cropY,
      cropWidth,
      cropHeight,
      0,
      0,
      croppedCanvas.width,
      croppedCanvas.height
    );

    // Apply shape mask and effects
    if (cropShape === 'ellipse') {
      ctx.globalCompositeOperation = 'destination-in';
      
      if (feather > 0) {
        // Apply feathered ellipse mask using transformed radial gradient
        const centerX = croppedCanvas.width / 2;
        const centerY = croppedCanvas.height / 2;
        const radiusX = croppedCanvas.width / 2;
        const radiusY = croppedCanvas.height / 2;
        const featherPercent = Math.min(40, feather * 0.4) / 100;
        const innerRadius = radiusX * (1 - featherPercent);

        const maskCanvas = document.createElement('canvas');
        maskCanvas.width = croppedCanvas.width;
        maskCanvas.height = croppedCanvas.height;
        const maskCtx = maskCanvas.getContext('2d');
        if (maskCtx) {
          maskCtx.save();
          maskCtx.translate(centerX, centerY);
          if (radiusX !== 0) {
            maskCtx.scale(1, radiusY / radiusX);
          }
          const gradient = maskCtx.createRadialGradient(0, 0, Math.max(0, innerRadius), 0, 0, radiusX);
          gradient.addColorStop(0, 'rgba(255, 255, 255, 1)');
          gradient.addColorStop(1, 'rgba(255, 255, 255, 0)');

          maskCtx.fillStyle = gradient;
          maskCtx.beginPath();
          maskCtx.arc(0, 0, radiusX, 0, 2 * Math.PI);
          maskCtx.fill();
          maskCtx.restore();

          ctx.drawImage(maskCanvas, 0, 0);
        }
      } else {
        // Hard edge ellipse
        ctx.beginPath();
        ctx.ellipse(
          croppedCanvas.width / 2,
          croppedCanvas.height / 2,
          croppedCanvas.width / 2,
          croppedCanvas.height / 2,
          0,
          0,
          2 * Math.PI
        );
        ctx.fill();
      }
      
      // Apply border for ellipse if needed
      if (borderWidth > 0) {
        ctx.globalCompositeOperation = 'source-over';
        ctx.strokeStyle = borderColor;
        ctx.lineWidth = borderWidth;
        ctx.beginPath();
        ctx.ellipse(
          croppedCanvas.width / 2,
          croppedCanvas.height / 2,
          croppedCanvas.width / 2 - borderWidth / 2,
          croppedCanvas.height / 2 - borderWidth / 2,
          0,
          0,
          2 * Math.PI
        );
        ctx.stroke();
      }
    } else {
      // Rectangle crop with optional feather
      if (feather > 0) {
        const featherPercent = Math.min(40, feather * 0.4) / 100;
        const featherDist = Math.max(1, Math.min(croppedCanvas.width, croppedCanvas.height) * featherPercent);
        
        const maskCanvas = document.createElement('canvas');
        maskCanvas.width = croppedCanvas.width;
        maskCanvas.height = croppedCanvas.height;
        const maskCtx = maskCanvas.getContext('2d');
        
        if (maskCtx) {
          // Fill entire canvas with white first
          maskCtx.fillStyle = 'white';
          maskCtx.fillRect(0, 0, croppedCanvas.width, croppedCanvas.height);
          
          // Use globalCompositeOperation to apply feather with alpha gradients
          maskCtx.globalCompositeOperation = 'destination-out';
          
          // Draw feather gradients that fade OUT from the edges
          // Top edge
          const gradTop = maskCtx.createLinearGradient(0, 0, 0, featherDist);
          gradTop.addColorStop(0, 'rgba(0,0,0,1)');
          gradTop.addColorStop(1, 'rgba(0,0,0,0)');
          maskCtx.fillStyle = gradTop;
          maskCtx.fillRect(0, 0, croppedCanvas.width, featherDist);
          
          // Bottom edge
          const gradBottom = maskCtx.createLinearGradient(0, croppedCanvas.height - featherDist, 0, croppedCanvas.height);
          gradBottom.addColorStop(0, 'rgba(0,0,0,0)');
          gradBottom.addColorStop(1, 'rgba(0,0,0,1)');
          maskCtx.fillStyle = gradBottom;
          maskCtx.fillRect(0, croppedCanvas.height - featherDist, croppedCanvas.width, featherDist);
          
          // Left edge
          const gradLeft = maskCtx.createLinearGradient(0, 0, featherDist, 0);
          gradLeft.addColorStop(0, 'rgba(0,0,0,1)');
          gradLeft.addColorStop(1, 'rgba(0,0,0,0)');
          maskCtx.fillStyle = gradLeft;
          maskCtx.fillRect(0, 0, featherDist, croppedCanvas.height);
          
          // Right edge
          const gradRight = maskCtx.createLinearGradient(croppedCanvas.width - featherDist, 0, croppedCanvas.width, 0);
          gradRight.addColorStop(0, 'rgba(0,0,0,0)');
          gradRight.addColorStop(1, 'rgba(0,0,0,1)');
          maskCtx.fillStyle = gradRight;
          maskCtx.fillRect(croppedCanvas.width - featherDist, 0, featherDist, croppedCanvas.height);
          
          maskCtx.globalCompositeOperation = 'source-over';
          
          // Apply mask
          ctx.globalCompositeOperation = 'destination-in';
          ctx.drawImage(maskCanvas, 0, 0);
          ctx.globalCompositeOperation = 'source-over';
        }
      }
      
      // Apply border for rectangle if needed
      if (borderWidth > 0) {
        ctx.globalCompositeOperation = 'source-over';
        ctx.strokeStyle = borderColor;
        ctx.lineWidth = borderWidth;
        if (borderStyle === 'dashed') {
          ctx.setLineDash([5, 5]);
        } else if (borderStyle === 'dotted') {
          ctx.setLineDash([2, 2]);
        }
        ctx.strokeRect(borderWidth / 2, borderWidth / 2, croppedCanvas.width - borderWidth, croppedCanvas.height - borderWidth);
      }
    }

    // Convert to data URL
    const croppedDataUrl = croppedCanvas.toDataURL('image/png');
    onCrop(croppedDataUrl, { name: croppedName, shape: cropShape, feather });
  };

  return (
    <div className="crop-modal-overlay" onClick={onClose}>
      <div className="crop-modal-content crop-modal-wide" onClick={(e) => e.stopPropagation()}>
        <div className="crop-modal-header">
          <h3>Crop Image</h3>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>
        
        <div className="crop-modal-body crop-modal-body-horizontal">
          {/* Left side: Image preview */}
          <div className="crop-preview-section">
            <div className="crop-instructions">
              Drag the crop area to reposition. Drag the blue square to resize.
            </div>
            <canvas
              ref={canvasRef}
              className="crop-canvas"
              onMouseDown={handleMouseDown}
              onMouseMove={handleMouseMove}
              onMouseUp={handleMouseUp}
              onMouseLeave={handleMouseUp}
            />
          </div>
          
          {/* Right side: Controls */}
          <div className="crop-controls-section">
            <div className="crop-name-input">
              <label>
                Cropped Image Name:
                <input
                  type="text"
                  value={croppedName}
                  onChange={(e) => setCroppedName(e.target.value)}
                  placeholder="Enter name for cropped image"
                />
              </label>
            </div>
            
            <div className="crop-shape-selector">
              <label className="crop-shape-label">Shape:</label>
              <label>
                <input
                  type="radio"
                  value="rect"
                  checked={cropShape === 'rect'}
                  onChange={(e) => setCropShape(e.target.value as 'rect' | 'ellipse')}
                />
                Rectangle
              </label>
              <label>
                <input
                  type="radio"
                  value="ellipse"
                  checked={cropShape === 'ellipse'}
                  onChange={(e) => setCropShape(e.target.value as 'rect' | 'ellipse')}
                />
                Ellipse/Circle
              </label>
            </div>
            
            <div className="crop-feather-control">
              <label>
                Edge Feather: {feather}
                <input
                  type="range"
                  min="0"
                  max="100"
                  value={feather}
                  onChange={(e) => setFeather(Number(e.target.value))}
                  className="feather-slider"
                />
              </label>
              <p className="help-text">Softens the edges for a smooth transition</p>
            </div>
            
            <div className="crop-border-controls">
              <h4>Border</h4>
              <div className="border-row">
                <label>
                  Width:
                  <input
                    type="number"
                    min="0"
                    max="20"
                    value={borderWidth}
                    onChange={(e) => setBorderWidth(Number(e.target.value))}
                  />
                </label>
                <label>
                  Style:
                  <select value={borderStyle} onChange={(e) => setBorderStyle(e.target.value)}>
                    <option value="solid">Solid</option>
                    <option value="dashed">Dashed</option>
                    <option value="dotted">Dotted</option>
                  </select>
                </label>
                <label>
                  Color:
                  <input
                    type="color"
                    value={borderColor}
                    onChange={(e) => setBorderColor(e.target.value)}
                  />
                </label>
              </div>
            </div>
          </div>
        </div>

        <div className="crop-modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button className="btn btn-primary" onClick={handleApplyCrop}>
            Apply Crop
          </button>
        </div>
      </div>
    </div>
  );
};

export default CropModal;
